// Invite People, from the program's People tab. Two ways in, as in Update Team:
//   • Existing: someone already in the organization joins the program now
//     (POST org/{authId}/members/{userId}/programs).
//   • New: someone invited by email to the organization and this program, the
//     Org Members invitation (POST org/{authId}/members/invite with this
//     program's id), with their team and their role in it.
// A program for roles takes its learners through their roles (inviteChoices.ts):
// New invites a learner only with their team and one of the program's roles
// in it, and they join the program through that role when they sign up.
// Existing adds only an admin to it (the backend refuses a learner with 409).

import React, { useEffect, useMemo, useState } from 'react';
import { Box, Dialog, DialogActions, DialogContent, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import { API } from '../../../../../../../api';
import { capabilityApi } from '../../../../../../../api/capability';
import CommonModalHeader from '../../../../../../../components/DialogUi/CommonModalHeader';
import DosenButton from '../../../../../../../components/ui/Buttons/DosenButton';
import DosenTooltip from '../../../../../../../components/ui/DosenTooltip';
import { useProgramContext } from '../../../../../../../hooks/useProgramContext';
import { useAppSelector } from '../../../../../../../redux/hooks';
import { attributeKeys, events } from '../../../../../../../utils/amplitude';
import { trackWithBaseProps } from '../../../../../../../utils/amplitudeProductTracking';
import { useRoleLabels } from '../../../../../../../utils/memberTypeLabels';
import DosenAutocomplete from '../../../../../components/DosenAutocomplete';
import DosenInput from '../../../../../components/DosenInput';
import type { ProgramCardData, ProgramOwner } from '../types';
import { ORANGE, t } from './editorUi';
import {
  joinsThroughRole,
  programRoleValues,
  roleChoices,
  teamAndRoleValid,
  teamChoices,
  type InviteRole,
  type InviteTeam,
  type InviteWay,
  type ProgramRoleValue
} from './inviteChoices';

interface Option {
  value: string;
  label: string;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const subcopySx = { fontSize: 12, color: '#868686', fontFamily: 'Inter, sans-serif', mt: 0.5 };

const WayButton: React.FC<{ label: string; selected: boolean; onClick: () => void }> = ({
  label,
  selected,
  onClick
}) => (
  <Box
    component="button"
    type="button"
    aria-pressed={selected}
    onClick={onClick}
    sx={{
      flex: 1,
      py: '8px',
      borderRadius: '6px',
      border: `1px solid ${selected ? ORANGE : t.borderDim}`,
      bgcolor: selected ? 'rgba(247, 148, 32, 0.12)' : t.surface,
      color: selected ? t.textPrimary : t.textSecondary,
      fontFamily: 'Inter, sans-serif',
      fontSize: 13,
      fontWeight: 600,
      cursor: 'pointer'
    }}
  >
    {label}
  </Box>
);

const InvitePeopleDialog: React.FC<{
  open: boolean;
  program: ProgramCardData;
  /** Org members not already in the program. */
  choices: ProgramOwner[];
  onClose: () => void;
  onInvited: () => void;
}> = ({ open, program, choices, onClose, onInvited }) => {
  const { orgId } = useProgramContext();
  const authId = useAppSelector((s) => s.orgDetails.data?.authId) || orgId;
  const labels = useRoleLabels();
  const forRoles = program.roleIds.length > 0;

  const [way, setWay] = useState<InviteWay>('existing');
  const [person, setPerson] = useState<Option | null>(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [chosenRole, setChosenRole] = useState<ProgramRoleValue>('mentee');
  const [team, setTeam] = useState<Option | null>(null);
  const [teamRole, setTeamRole] = useState<Option | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // The organization's teams and the roles in each, read once the dialog opens.
  const [teams, setTeams] = useState<InviteTeam[]>([]);
  const [roles, setRoles] = useState<InviteRole[]>([]);

  // A course has no mentors: a person joins it as a learner or as one of its admins. Existing adds
  // only an admin to a program for roles; the role chosen comes back when they choose New again.
  const programRoles: Option[] = programRoleValues(forRoles, way).map((value) => ({
    value,
    label: value === 'admin' ? labels.admin : labels.mentee
  }));
  const programRole = programRoles.find((o) => o.value === chosenRole) ?? programRoles[0];
  // A learner new to a program for roles joins it through one of its roles: their team and that role are required.
  const throughRole = joinsThroughRole(forRoles, way, programRole.value as ProgramRoleValue);
  const programRoleIds = throughRole ? program.roleIds : null;
  // A program for roles offers only its own roles (its audiences), whether they join as a learner or an admin.
  const listedRoleIds = forRoles ? program.roleIds : null;

  useEffect(() => {
    if (!open) return;
    capabilityApi
      .operations(undefined, orgId)
      .then((view) => {
        setTeams(view.teams ?? []);
        setRoles(view.roles ?? []);
      })
      // No teams to choose from (no capability set up): New invites without one.
      .catch(() => {
        setTeams([]);
        setRoles([]);
      });
  }, [open, orgId]);

  const people: Option[] = useMemo(() => choices.map((m) => ({ value: m.id, label: m.name })), [choices]);
  // For a program for roles, only the teams with one of the program's roles confirmed, and only those roles.
  const teamOptions: Option[] = teamChoices(teams, roles, listedRoleIds).map((x) => ({ value: x.id, label: x.name }));
  // People are invited to a role once its capabilities and levels are confirmed (as in Invite Team).
  const teamRoleOptions: Option[] = roleChoices(roles, team?.value, listedRoleIds).map((r) => ({
    value: r.id,
    label: r.name
  }));

  const reset = () => {
    setWay('existing');
    setPerson(null);
    setFirstName('');
    setLastName('');
    setEmail('');
    setChosenRole('mentee');
    setTeam(null);
    setTeamRole(null);
  };
  const close = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  // A team needs the role the person holds in it; joining through a role needs both, of the program's.
  const teamReady = teamAndRoleValid({
    teams,
    roles,
    teamId: team?.value,
    roleId: teamRole?.value,
    programRoleIds
  });
  const valid =
    way === 'existing'
      ? !!person
      : firstName.trim().length > 0 && lastName.trim().length > 0 && EMAIL_RE.test(email.trim()) && teamReady;
  // Why Send Invite waits, when it is the team and role a learner of a program for roles still needs.
  const sendHint =
    way === 'new' && throughRole && !teamReady
      ? teamOptions.length === 0
        ? "None of this program's roles is confirmed in a team yet, so a learner can't be invited to it."
        : "Choose their team and one of the program's roles in it."
      : '';

  const submit = async () => {
    if (!valid || !authId) return;
    setSubmitting(true);
    try {
      if (way === 'existing' && person) {
        await API.allocateMemberToProgram(authId, person.value, { programId: program.id, role: [programRole.value] });
        toast.success(`${person.label} is added to ${program.name}.`);
      } else {
        const address = email.trim().toLowerCase();
        await API.inviteOrgMember(authId, {
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          email: address,
          role: [programRole.value],
          programIds: [program.id],
          ...(team && teamRole ? { teamId: team.value, roleId: teamRole.value } : {})
        });
        trackWithBaseProps(events.userInvited, {
          inviteeEmail: address,
          inviteeRole: programRole.value,
          [attributeKeys.enrollmentType]: 'Manual Invite',
          [attributeKeys.programName]: program.name
        });
        toast.success(
          throughRole
            ? `${firstName.trim()} is invited. They join ${program.name} through their role when they sign up.`
            : `${firstName.trim()} is invited to ${program.name}.`
        );
      }
      onInvited();
      reset();
      onClose();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'The invitation could not be sent. Try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: '#0F0F0F',
          color: '#FDFDFD',
          border: '1px solid #2A2A2A',
          borderRadius: 2,
          width: 480,
          maxWidth: '90vw'
        }
      }}
    >
      <CommonModalHeader title="Invite People" handleCloseModal={close} />
      <DialogContent style={{ backgroundColor: '#0F0F0F' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
          <Box sx={{ display: 'flex', gap: '8px' }}>
            <WayButton label="Existing" selected={way === 'existing'} onClick={() => setWay('existing')} />
            <WayButton label="New" selected={way === 'new'} onClick={() => setWay('new')} />
          </Box>

          {way === 'existing' ? (
            <Box>
              <DosenAutocomplete
                label="Person*"
                options={people}
                value={person}
                onChange={(_: React.SyntheticEvent, value: Option | null) => setPerson(value)}
                getOptionLabel={(option: Option) => option?.label ?? ''}
                isOptionEqualToValue={(a: Option, b: Option) => a.value === b.value}
                placeholder="Choose someone in your organization"
              />
              <Typography sx={subcopySx}>
                They join this program now, whether or not they match its audience.
              </Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <DosenInput
                  label="First name*"
                  placeholder="John"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
                <DosenInput
                  label="Last name*"
                  placeholder="Smith"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </Box>
              <DosenInput
                label="Email*"
                placeholder="john.smith@gmail.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              {throughRole && teamOptions.length === 0 ? (
                <Typography sx={subcopySx}>
                  Learners join this program through its roles, and none of them is confirmed in a team yet. Confirm one
                  of them on the Capabilities page, or invite an admin.
                </Typography>
              ) : (
                teamOptions.length > 0 && (
                  <Box>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <DosenAutocomplete
                          label={throughRole ? 'Team*' : 'Team'}
                          options={teamOptions}
                          value={team}
                          onChange={(_: React.SyntheticEvent, value: Option | null) => {
                            setTeam(value);
                            setTeamRole(null);
                          }}
                          getOptionLabel={(option: Option) => option?.label ?? ''}
                          isOptionEqualToValue={(a: Option, b: Option) => a.value === b.value}
                          placeholder="Choose a team"
                        />
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <DosenAutocomplete
                          label={team || throughRole ? 'Role in team*' : 'Role in team'}
                          options={teamRoleOptions}
                          value={teamRole}
                          // Never disabled (that turns the field white): with no team yet it lists nothing and says why.
                          noOptionsText={
                            !team
                              ? 'Choose a team first'
                              : forRoles
                              ? "None of this program's roles is confirmed in this team yet"
                              : 'This team has no confirmed roles yet'
                          }
                          onChange={(_: React.SyntheticEvent, value: Option | null) => setTeamRole(value)}
                          getOptionLabel={(option: Option) => option?.label ?? ''}
                          isOptionEqualToValue={(a: Option, b: Option) => a.value === b.value}
                          placeholder="Choose a role"
                        />
                      </Box>
                    </Box>
                    <Typography sx={subcopySx}>
                      {throughRole
                        ? "Learners join this program through its roles: choose their team and one of the program's roles there. They join it when they sign up."
                        : 'You can invite people to a role once its capabilities and levels are confirmed.'}
                    </Typography>
                  </Box>
                )
              )}
            </>
          )}

          <Box>
            <DosenAutocomplete
              label="Program role*"
              options={programRoles}
              value={programRole}
              disableClearable
              onChange={(_: React.SyntheticEvent, value: Option) => {
                if (!value) return;
                // The teams and roles offered are the same for a learner and an admin: the choice stays.
                setChosenRole(value.value as ProgramRoleValue);
              }}
              getOptionLabel={(option: Option) => option?.label ?? ''}
              isOptionEqualToValue={(a: Option, b: Option) => a.value === b.value}
            />
            {forRoles && way === 'existing' && (
              <Typography sx={subcopySx}>
                Learners join this program through its roles, so you can add admins here. To invite someone new as a
                learner, choose New.
              </Typography>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions style={{ backgroundColor: '#0F0F0F' }}>
        <DosenTooltip title={sendHint} placement="top">
          {/* A span, so the hint still shows on the disabled button. */}
          <span>
            <DosenButton
              tone="cta"
              onClick={submit}
              disabled={submitting || !valid}
              loading={submitting}
              loadingText={way === 'existing' ? 'Adding...' : 'Inviting...'}
            >
              {way === 'existing' ? 'Add to Program' : 'Send Invite'}
            </DosenButton>
          </span>
        </DosenTooltip>
      </DialogActions>
    </Dialog>
  );
};

export default InvitePeopleDialog;
